import { Component, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, onSnapshot } from '@angular/fire/firestore';
import { Subscription } from 'rxjs';
import { AuthService } from './core/services/auth.service';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private translate  = inject(TranslateService);
  private auth       = inject(Auth);
  private firestore  = inject(Firestore);
  private authSvc    = inject(AuthService);
  private router     = inject(Router);
  private zone       = inject(NgZone);

  private authSub?:     Subscription;
  private unsubSnap?:   () => void;
  private resumeSub?:   { remove: () => Promise<void> };
  private urlOpenSub?:  { remove: () => Promise<void> };

  constructor() {
    // Aplicar idioma persistido antes de que cargue cualquier vista.
    // Si no hay preferencia guardada, español por defecto.
    const idioma = localStorage.getItem('rc_idioma') ?? 'es';
    this.translate.use(idioma).subscribe();
  }

  /**
   * Guardia de sesión en tiempo real.
   *
   * Flujo:
   * 1. authState emite cada vez que Firebase Auth cambia (login, logout, token refresh).
   * 2. Cuando hay usuario activo, se abre un listener en tiempo real sobre usuarios/{uid}.
   * 3. Si el admin cambia activo:false o permitirLogin:false mientras la sesión está abierta,
   *    el listener detecta el cambio y fuerza logout inmediatamente sin esperar a que el
   *    usuario recargue la página.
   * 4. Al hacer logout (o cambiar de usuario), el listener anterior se cancela antes de
   *    abrir uno nuevo para evitar listeners huérfanos.
   *
   * Por qué onSnapshot y no un guard de ruta: los guards solo corren al navegar.
   * Este listener cubre el caso de una sesión ya abierta que debe ser revocada.
   */
  ngOnInit(): void {
    // Android resetea los flags de ventana al volver de la pantalla de recientes,
    // lo que causa que env(safe-area-inset-top) fluctúe y el ion-header salte.
    // Se aplica en init y en cada resume para mantener el overlay consistente.
    if (Capacitor.isNativePlatform()) {
      this.applyStatusBar();
      App.addListener('resume', () => this.applyStatusBar()).then(h => this.resumeSub = h);

      // Deep link: el correo de restablecer contraseña apunta a app.resi-connect.com/auth/reset-password.
      // Universal Links (iOS) / App Links (Android) abren la app y aquí se captura el URL entrante
      // para navegar a la pantalla con el oobCode en vez de dejarlo en el navegador.
      App.addListener('appUrlOpen', ({ url }) => this.handleDeepLink(url))
        .then(h => this.urlOpenSub = h);

      // Arranque en frío: si la app estaba cerrada, appUrlOpen puede no dispararse; getLaunchUrl
      // recupera el URL con el que se abrió la app.
      App.getLaunchUrl().then(res => { if (res?.url) this.handleDeepLink(res.url); });
    }

    this.authSub = authState(this.auth).subscribe(user => {
      // Cancelar listener anterior antes de abrir uno nuevo (o al cerrar sesión)
      this.unsubSnap?.();
      this.unsubSnap = undefined;

      if (!user) return;

      this.unsubSnap = onSnapshot(
        doc(this.firestore, `usuarios/${user.uid}`),
        snapshot => {
          if (!snapshot.exists()) return;
          if (snapshot.metadata.fromCache) return;
          const data          = snapshot.data();
          const activo        = data['activo']        as boolean;
          const permitirLogin = data['permitirLogin'] as boolean;
          // activo:false = soft delete | permitirLogin:false = acceso bloqueado por el admin
          if (activo === false || permitirLogin === false) {
            this.authSvc.logout();
          }
        }
      );
    });
  }

  ngOnDestroy(): void {
    this.unsubSnap?.();
    this.authSub?.unsubscribe();
    this.resumeSub?.remove();
    this.urlOpenSub?.remove();
  }

  // Extrae la ruta y los query params (oobCode, lang, mode…) del URL entrante y navega a
  // reset-password. El evento llega fuera de la zona de Angular, por eso zone.run reengancha
  // el change detection para que la navegación surta efecto.
  private handleDeepLink(url: string): void {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return;
    }
    if (!parsed.pathname.startsWith('/auth/reset-password')) return;

    const queryParams: Record<string, string> = {};
    parsed.searchParams.forEach((value, key) => { queryParams[key] = value; });
    this.zone.run(() => this.router.navigate(['/auth/reset-password'], { queryParams }));
  }

  // overlaysWebView: WebView se extiende bajo la status bar (evita el salto del header).
  // Style.Light: íconos de la status bar en blanco, acorde al fondo oscuro del header.
  private async applyStatusBar(): Promise<void> {
    await StatusBar.setOverlaysWebView({ overlay: true });
    await StatusBar.setStyle({ style: Style.Light });
  }
}
